import * as React from 'react';
import { mediaUnits } from '@ooh/contracts';
import { useApiQuery } from '../../data/use-api-query';
import type { EntriesGridData } from '../../availability/lib/availability-types';
import type { InventoryOccupancyByRowId } from '../inventory/components/inventory-availability-shared';
import type { Granularity } from '../inventory/components/availability-granularity-select';
import { buildPeriods } from '../inventory/lib/build-periods';
import {
  mapAvailabilityResponseToEntriesGrid,
  mapAvailabilityResponseToOccupancy,
} from './lib/availability-mapping';
import { buildAvailabilityWindow } from './lib/build-availability-window';
import { mediaUnitAvailabilityQueryKey } from './lib/query-keys';

const WINDOW_STEP_MONTHS = 6;
const NO_OCCUPANCY: InventoryOccupancyByRowId = {};
const NO_ENTRIES: EntriesGridData = {};

export function useDetailAvailabilityData(unitId: number, canViewCalendar: boolean) {
  const [windowOffsetMonths, setWindowOffsetMonths] = React.useState(0);
  const [granularity, setGranularity] = React.useState<Granularity>('month');
  const window = buildAvailabilityWindow(windowOffsetMonths);

  const { data } = useApiQuery(
    mediaUnits.getAvailability,
    { unitIds: [unitId], from: window.from, to: window.to },
    {
      enabled: canViewCalendar && unitId > 0,
      queryKey: mediaUnitAvailabilityQueryKey([unitId], window.from, window.to),
    },
  );

  const occupancyByRowId = React.useMemo(
    () => (data ? mapAvailabilityResponseToOccupancy(data) : NO_OCCUPANCY),
    [data],
  );
  // Period ids come from the YYYY-MM prefix, so the dashed window-sourced borders only line up at month granularity.
  const entriesGrid = React.useMemo(
    () => (data ? mapAvailabilityResponseToEntriesGrid(data) : NO_ENTRIES),
    [data],
  );
  const periods = React.useMemo(
    () => buildPeriods(granularity, 12, new Date(window.from)),
    [granularity, window.from],
  );

  return {
    occupancyByRowId,
    entriesGrid,
    periods,
    granularity,
    onGranularityChange: setGranularity,
    windowStartDate: React.useMemo(() => new Date(window.from), [window.from]),
    onPrevWindow: React.useCallback(() => setWindowOffsetMonths((n) => n - WINDOW_STEP_MONTHS), []),
    onNextWindow: React.useCallback(() => setWindowOffsetMonths((n) => n + WINDOW_STEP_MONTHS), []),
  };
}
